import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { liveDataAPI } from '../services/api';
import { MarketData } from '../types';
import { RefreshCw, TrendingUp, TrendingDown, Activity, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Container, Card, Table, Spinner, Button, Form, InputGroup } from 'react-bootstrap';
import MainLayout from '../components/MainLayout';

const MarketDataPage: React.FC = () => {
  const navigate = useNavigate();
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchLiveData();
      setLoading(false);
    };

    loadInitialData();
    const interval = setInterval(fetchLiveData, 2000);
    return () => clearInterval(interval);
  }, [currentPage, searchTerm]);

  useEffect(() => {
    if (searchInput.length >= 3) {
      const debounceTimer = setTimeout(() => {
        fetchSuggestions(searchInput);
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchInput]);

  const fetchSuggestions = async (query: string) => {
    try {
      const results = await liveDataAPI.searchSymbols(query);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const fetchLiveData = async () => {
    try {
      console.log('[MarketDataPage] fetchLiveData called');
      setRefreshing(true);
      const startTime = Date.now();

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 1000)
      );

      const dataPromise = liveDataAPI.getLiveData(currentPage, pageSize, searchTerm);
      const response = await Promise.race([dataPromise, timeoutPromise]) as any;

      console.log('[MarketDataPage] Response received:', response);
      setMarketData(response.data || response);
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
      }

      const loadTime = Date.now() - startTime;
      console.log(`Data loaded in ${loadTime}ms`, new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Failed to fetch live data:', error);
    } finally {
      setLastUpdate(new Date());
      setRefreshing(false);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (symbol: string) => {
    setSearchInput(symbol);
    setSearchTerm(symbol);
    setCurrentPage(1);
    setShowSuggestions(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
  };

  const getTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;

    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return { date: dateStr, time: timeStr };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <div className="mt-2" style={{ fontSize: '13px', fontWeight: '500' }}>Loading Market Data...</div>
        </div>
      </div>
    );
  }

  return (
    <MainLayout>
      {/* Header */}
      <div style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px', margin: '16px', padding: '12px' }}>
        <Container>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontWeight: '600', fontSize: '18px', color: '#212529' }}>
                Live Market Data
              </h2>
              <p style={{ margin: '0', fontSize: '12px', color: '#6c757d' }}>Real-time stock prices and market information</p>
            </div>
            <Button
              variant="primary"
              onClick={fetchLiveData}
              disabled={refreshing}
              style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '4px' }}
            >
              <RefreshCw size={14} className={`me-1 ${refreshing ? 'spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </Container>
      </div>

      <Container className="pb-4">
        {/* Search Bar - TODO: Add this feature later */}
        {/* <Card style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <Card.Body className="p-3">
            <div style={{ position: 'relative' }}>
              <InputGroup size="sm" style={{ borderRadius: '6px', overflow: 'hidden' }}>
                <InputGroup.Text style={{ background: '#f8f9fa', border: 'none', padding: '8px 12px' }}>
                  <Search size={16} color="#6c757d" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search symbols... (e.g., RELIANCE, TCS)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  style={{
                    fontSize: '14px',
                    border: 'none',
                    boxShadow: 'none',
                    padding: '8px 12px'
                  }}
                />
                {searchInput && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSearchInput('');
                      setSearchTerm('');
                      setCurrentPage(1);
                      setShowSuggestions(false);
                      fetchLiveData();
                    }}
                    style={{ border: 'none', background: 'transparent', padding: '8px 12px' }}
                  >
                    <span style={{ fontSize: '18px', color: '#6c757d' }}>×</span>
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={handleSearch}
                  style={{ border: 'none', padding: '8px 16px', background: '#007bff' }}
                >
                  Search
                </Button>
              </InputGroup>

              {showSuggestions && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {suggestions.map((symbol, index) => (
                    <div
                      key={symbol}
                      onClick={() => handleSuggestionClick(symbol)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        borderBottom: index < suggestions.length - 1 ? '1px solid #f1f3f4' : 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      {symbol}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card.Body>
        </Card> */}

        {/* Market Data Table */}
        <Card style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: '4px' }}>
          <Card.Header className="bg-transparent border-0 p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div style={{ background: '#007bff', borderRadius: '4px', padding: '4px', color: 'white', marginRight: '8px' }}>
                  <Activity size={16} />
                </div>
                <h5 style={{ margin: '0', fontWeight: '600', fontSize: '14px' }}>Market Data (Page {currentPage} of {totalPages})</h5>
              </div>
              <small style={{ color: '#6c757d', fontSize: '11px' }}>Last updated: {lastUpdate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</small>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {marketData.length > 0 ? (
              <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Table responsive className="mb-0" style={{ fontSize: '12px' }}>
                  <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '12%' }}>Symbol</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '11%' }}>LTP</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '10%' }}>High</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '10%' }}>Low</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '10%' }}>Open</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '10%' }}>Close</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '10%' }}>Change %</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '10%' }}>Volume</th>
                      <th style={{ fontWeight: '600', padding: '8px', fontSize: '11px', width: '17%' }}>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marketData.map((data, index) => {
                      const isPositive = data.change >= 0;
                      const timestamp = (data as any).timestamp;
                      const timeAgo = timestamp ? getTimeAgo(new Date(timestamp)) : '-';

                      return (
                        <tr key={data.symbol} style={{
                          borderBottom: '1px solid #f1f3f4',
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                        }}>
                          <td style={{ fontWeight: '600', padding: '6px 8px', color: '#212529' }}>{data.symbol}</td>
                          <td style={{ padding: '6px 8px', fontWeight: '600', color: '#007bff', fontSize: '12px' }}>
                            {data.ltp > 0 ? formatCurrency(data.ltp) : '-'}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#28a745', fontSize: '11px' }}>
                            {data.high > 0 ? formatCurrency(data.high) : '-'}
                          </td>
                          <td style={{ padding: '6px 8px', color: '#dc3545', fontSize: '11px' }}>
                            {data.low > 0 ? formatCurrency(data.low) : '-'}
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: '11px' }}>
                            {data.open > 0 ? formatCurrency(data.open) : '-'}
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: '11px', fontWeight: '500' }}>
                            {(data as any).close > 0 ? formatCurrency((data as any).close) : '-'}
                          </td>
                          <td style={{
                            padding: '6px 8px',
                            fontWeight: '600',
                            color: isPositive ? '#28a745' : '#dc3545',
                            fontSize: '11px'
                          }}>
                            <div className="d-flex align-items-center">
                              {isPositive ? <TrendingUp size={10} className="me-1" /> : <TrendingDown size={10} className="me-1" />}
                              {data.change_percentage !== 0 ? formatPercentage(data.change_percentage) : '-'}
                            </div>
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: '11px' }}>
                            {data.volume > 0 ? data.volume.toLocaleString() : '-'}
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: '10px' }}>
                            {timestamp ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <div style={{
                                  background: '#f8f9fa',
                                  color: '#212529',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontWeight: '600',
                                  fontSize: '11px',
                                  textAlign: 'center',
                                  border: '1px solid #e9ecef',
                                  fontFamily: 'monospace'
                                }}>
                                  {formatDateTime(timestamp).time}
                                </div>
                                <div style={{
                                  color: '#868e96',
                                  fontSize: '9px',
                                  textAlign: 'center',
                                  fontWeight: '500',
                                  letterSpacing: '0.3px'
                                }}>
                                  {formatDateTime(timestamp).date}
                                </div>
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-4">
                <div style={{ color: '#6c757d' }}>
                  <div style={{ background: '#6c757d', borderRadius: '4px', padding: '12px', color: 'white', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                    <Activity size={24} />
                  </div>
                  <h6 style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>No market data available</h6>
                  <small style={{ fontSize: '11px' }}>Market data will appear here when available</small>
                </div>
              </div>
            )}
          </Card.Body>

          {/* Pagination */}
          {totalPages > 1 && (
            <Card.Footer className="bg-transparent border-0 p-3">
              <div className="d-flex justify-content-between align-items-center">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={14} className="me-1" />
                  Previous
                </Button>

                <span style={{ fontSize: '13px', color: '#6c757d' }}>
                  Page {currentPage} of {totalPages}
                </span>

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight size={14} className="ms-1" />
                </Button>
              </div>
            </Card.Footer>
          )}
        </Card>
      </Container>
    </MainLayout>
  );
};

export default MarketDataPage;